<script setup>
import { userStore } from '@/stores/user'
import { RouterView, useRouter } from 'vue-router'
import { fetchFromApi } from '@/fetch.ts'
import { ref, onDeactivated, onMounted, computed } from 'vue';
import Button from 'primevue/button';
import ButtonGroup from 'primevue/buttongroup';
import Card from 'primevue/card';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import ProgressBar from 'primevue/progressbar';
import ProgressSpinner from 'primevue/progressspinner';

import { setAffiliateId, getAffiliateId } from '@/composables/affiliateFunctions.ts';
import NewWidgetSubscriptionView from '@/views/NewWidgetSubscriptionView.vue';
import WidgetSubscriptionView from '@/views/WidgetSubscriptionView.vue';

const user = userStore();
const router = useRouter();

const showNewSubscriptionDialog = ref(false);
const subscriptions = ref([]);
const loading = ref(false);
const error = ref(null);
const bookmakers = ref([]);

onMounted(async () => {
    if (user.loggedIn === false) {
        router.push('/');
        return;
    }
    await Promise.all([
        fetchSubscriptions(),
        fetch('https://the-odds-api.com/bookmakers.json')
            .then(r => r.json())
            .then(data => { bookmakers.value = data; })
            .catch(err => console.error('Failed to fetch bookmakers:', err)),
    ]);
});

async function fetchSubscriptions() {
    loading.value = true;
    error.value = null;

    // Include affiliate info if available
    const ref = getAffiliateId();
    const queryParams = ref ? `?ref_id=${encodeURIComponent(ref.id)}&ref_dt=${encodeURIComponent(ref.dt)}` : '';

    try {
        const response = await fetchFromApi('/widget/subscriptions/' + queryParams);
        if (response.error) {
            error.value = response.error;
        } else {
            subscriptions.value = response.data;
        }
    } catch (err) {
        error.value = err;
    } finally {
        loading.value = false;
    }
}

const createFreeSubscriptionLoading = ref(false);
const createFreeSubscriptionError = ref(null);
async function createFreeSubscription() {
    createFreeSubscriptionLoading.value = true;
    createFreeSubscriptionError.value = null;
    try {
        const response = await fetchFromApi('/widget/subscriptions/', 'POST');
        if (response.error) {
            createFreeSubscriptionError.value = response.error;
        } else {
            subscriptions.value = [response.data];
        }
    } catch (err) {
        createFreeSubscriptionError.value = err;
    } finally {
        createFreeSubscriptionLoading.value = false;
    }
}

</script>

<template>

    <div class="flex justify-between items-center py-4 mb-4 mx-auto flex-col sm:flex-row" style="max-width: 800px;">
        <div class="text-xl font-semibold">Widget Subscriptions</div>
        <div class="flex items-center gap-2 mt-4 sm:mt-0">
            <Button 
                label="New Subscription"
                severity="info"
                icon="pi pi-plus"
                class="dark"
                size="small"
                @click="showNewSubscriptionDialog = true"
            />
            <Button 
                label="Docs"
                as="a"
                href="https://the-odds-api.com/widget/v1/#overview"
                target="_blank"
                rel="noopener"
                severity="secondary"
                icon="pi pi-book"
                class="border-black"
                size="small"
            />
        </div>
    </div>

    <Dialog v-model:visible="showNewSubscriptionDialog" modal :draggable="false" header="New Widget Subscription" class="w-4/5 bg-slate-100" style="max-width: 80rem;">
        <NewWidgetSubscriptionView />
    </Dialog>

    <p class="font-semibold text-sm mb-4 mx-auto flex justify-end" style="max-width: 800px;">
        <a class="text-blue-500 hover:underline" href="https://the-odds-api.com/guide/website-odds-integration.html" target="_blank">⚖️ Widget vs API Comparison</a>
    </p>

    <div v-if="loading" class="mt-20 flex justify-center">
        <ProgressSpinner strokeWidth="1px" />
    </div>
    <Message v-else-if="error" severity="error" class="mb-4">
        {{ error }}
    </Message>
    <Card v-else-if="subscriptions.length === 0" class="mb-4 m-4 text-slate-600">
        <template #content>
            No widget subscriptions found
            <Button 
                label="Create Free Subscription"
                severity="secondary"
                outlined
                class="my-6 border-black block"
                @click="createFreeSubscription"
                :loading="createFreeSubscriptionLoading"
                icon="pi pi-plus"
                size="small"
            />
            <Message v-if="createFreeSubscriptionError" severity="error" class="mt-2">
                {{ createFreeSubscriptionError }}
            </Message>
        </template>
    </Card>
    <WidgetSubscriptionView
        v-else v-for="subscription in subscriptions"
        :key="subscription.id"
        :subscription="subscription"
        :bookmakers="bookmakers"
        @showNewSubscription="showNewSubscriptionDialog = true"
    >
    </WidgetSubscriptionView>

</template>

